import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import './DashboardHome.css';

const DashboardHome = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Memoize fetchAnalytics to prevent unnecessary re-renders
  const fetchAnalytics = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      console.error('[DashboardHome] No authentication token available');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = {};
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      console.log('[DashboardHome] Fetching analytics from:', `${API_URL}/api/analytics/dashboard`);
      console.log('[DashboardHome] Token exists:', !!currentToken);
      console.log('[DashboardHome] Params:', params);

      const response = await axios.get(`${API_URL}/api/analytics/dashboard`, {
        params,
        headers: { Authorization: `Bearer ${currentToken}` }
      });

      console.log('[DashboardHome] Analytics response:', response.data);
      console.log('[DashboardHome] Response success:', response.data?.success);
      console.log('[DashboardHome] Response data:', response.data?.data);
      
      if (response.data && response.data.success && response.data.data) {
        console.log('[DashboardHome] ✅ Setting analytics data:', response.data.data);
        setAnalytics(response.data.data);
      } else {
        console.error('[DashboardHome] ❌ Invalid response structure:', response.data);
        console.error('[DashboardHome] Response keys:', Object.keys(response.data || {}));
        // Set default analytics structure even on invalid response
        setAnalytics(null);
        toast.error('Invalid response from server');
      }
    } catch (error) {
      console.error('[DashboardHome] Analytics fetch error:', error);
      console.error('[DashboardHome] Error response:', error.response?.data);
      console.error('[DashboardHome] Error status:', error.response?.status);
      console.error('[DashboardHome] Error message:', error.message);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch analytics';
      
      console.error('[DashboardHome] ❌ Error:', errorMessage);
      toast.error(errorMessage);
      
      // Set analytics to null on error so UI still renders with defaults
      setAnalytics(null);
      
      // If it's a module access error, show more details
      if (error.response?.data?.status === 'module_disabled') {
        toast.error(`Module '${error.response.data.moduleCode}' is not enabled for your tenant`);
      }
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);


  const formatCurrency = (amount, currency = 'INR') => {
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₹';
    return `${symbol}${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  // Helper function to check if analytics has any meaningful data
  const hasData = (analytics) => {
    if (!analytics) return false;
    
    const hasInventory = analytics.inventory && (
      analytics.inventory.totalProducts > 0 ||
      analytics.inventory.totalCategories > 0
    );
    const hasOrders = analytics.orders && analytics.orders.totalOrders > 0;
    const hasContent = analytics.content && analytics.content.totalBlogs > 0;
    const hasTopProducts = analytics.topProducts && analytics.topProducts.length > 0;
    const hasRevenue = analytics.revenueByCategory && analytics.revenueByCategory.length > 0;
    const hasRecentOrders = analytics.recentOrders && analytics.recentOrders.length > 0;
    const hasMonthlyRevenue = analytics.monthlyRevenue && analytics.monthlyRevenue.length > 0;
    
    return hasInventory || hasOrders || hasContent || hasTopProducts || hasRevenue || hasRecentOrders || hasMonthlyRevenue;
  };

  // Initialize default analytics structure if null
  const defaultAnalytics = {
    inventory: {
      totalProducts: 0,
      activeProducts: 0,
      lowStockProducts: 0,
      outOfStockProducts: 0,
      inventoryValue: 0,
      totalCategories: 0,
      activeCategories: 0,
      totalSubcategories: 0
    },
    orders: {
      totalOrders: 0,
      pendingOrders: 0,
      completedOrders: 0,
      totalRevenue: 0
    },
    content: {
      totalBlogs: 0,
      publishedBlogs: 0,
      totalBanners: 0,
      activeBanners: 0
    },
    topProducts: [],
    revenueByCategory: [],
    recentOrders: [],
    monthlyRevenue: []
  };

  const displayAnalytics = analytics || defaultAnalytics;
  const isEmpty = !hasData(analytics);

  if (loading) {
    return (
      <div className="dashboard-home">
        <div className="loading-container">
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-home">
      <div className="dashboard-header">
        <h1>Dashboard Overview</h1>
        <div className="date-filter">
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
            placeholder="Start Date"
          />
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
            placeholder="End Date"
          />
          <button onClick={() => setDateRange({ startDate: '', endDate: '' })} className="btn btn-secondary">
            Clear
          </button>
        </div>
      </div>

      {isEmpty && (
        <div className="empty-state-banner">
          <div className="empty-state-icon">📊</div>
          <h2>Welcome to Your Dashboard!</h2>
          <p>Your dashboard is ready, but there's no data to display yet. Start by adding products, categories, or creating your first order.</p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card inventory">
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <h3>Inventory</h3>
            <div className="stat-value">{displayAnalytics.inventory?.totalProducts || 0}</div>
            <div className="stat-details">
              <span>Active: {displayAnalytics.inventory?.activeProducts || 0}</span>
              <span className="warning">Low Stock: {displayAnalytics.inventory?.lowStockProducts || 0}</span>
              <span className="danger">Out of Stock: {displayAnalytics.inventory?.outOfStockProducts || 0}</span>
            </div>
            <div className="stat-footer">
              Value: {formatCurrency(displayAnalytics.inventory?.inventoryValue || 0)}
            </div>
          </div>
        </div>

        <div className="stat-card orders">
          <div className="stat-icon">🛒</div>
          <div className="stat-content">
            <h3>Orders</h3>
            <div className="stat-value">{displayAnalytics.orders?.totalOrders || 0}</div>
            <div className="stat-details">
              <span>Pending: {displayAnalytics.orders?.pendingOrders || 0}</span>
              <span className="success">Completed: {displayAnalytics.orders?.completedOrders || 0}</span>
            </div>
            <div className="stat-footer">
              Revenue: {formatCurrency(displayAnalytics.orders?.totalRevenue || 0)}
            </div>
          </div>
        </div>

        <div className="stat-card categories">
          <div className="stat-icon">📁</div>
          <div className="stat-content">
            <h3>Categories</h3>
            <div className="stat-value">{displayAnalytics.inventory?.totalCategories || 0}</div>
            <div className="stat-details">
              <span>Active: {displayAnalytics.inventory?.activeCategories || 0}</span>
              <span>Subcategories: {displayAnalytics.inventory?.totalSubcategories || 0}</span>
            </div>
            <Link to="/categories" className="stat-link">Manage Categories →</Link>
          </div>
        </div>

        <div className="stat-card content">
          <div className="stat-icon">📝</div>
          <div className="stat-content">
            <h3>Content</h3>
            <div className="stat-value">{displayAnalytics.content?.totalBlogs || 0}</div>
            <div className="stat-details">
              <span>Published: {displayAnalytics.content?.publishedBlogs || 0}</span>
              <span>Banners: {displayAnalytics.content?.activeBanners || 0}/{displayAnalytics.content?.totalBanners || 0}</span>
            </div>
            <Link to="/blogs" className="stat-link">Manage Content →</Link>
          </div>
        </div>
      </div>

      {/* Charts and Tables */}
      <div className="dashboard-grid">
        {/* Top Products */}
        <div className="dashboard-card">
          <h2>Top Selling Products</h2>
          {displayAnalytics.topProducts && displayAnalytics.topProducts.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Units Sold</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {displayAnalytics.topProducts.map((item, index) => (
                    <tr key={index}>
                      <td>{item.productName}</td>
                      <td>{item.totalSold}</td>
                      <td>{formatCurrency(item.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state-card">
              <div className="empty-state-icon-small">📦</div>
              <p className="no-data">No sales data available</p>
              <p className="no-data-hint">Start selling products to see top performers here</p>
              <Link to="/products" className="btn btn-primary" style={{ marginTop: '10px' }}>Add Products</Link>
            </div>
          )}
        </div>

        {/* Revenue by Category */}
        <div className="dashboard-card">
          <h2>Revenue by Category</h2>
          {displayAnalytics.revenueByCategory && displayAnalytics.revenueByCategory.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Orders</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {displayAnalytics.revenueByCategory.map((item, index) => (
                    <tr key={index}>
                      <td>{item.categoryName}</td>
                      <td>{item.orderCount}</td>
                      <td>{formatCurrency(item.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state-card">
              <div className="empty-state-icon-small">📁</div>
              <p className="no-data">No category revenue data available</p>
              <p className="no-data-hint">Revenue data will appear here once you start receiving orders</p>
            </div>
          )}
        </div>

        {/* Recent Orders */}
        <div className="dashboard-card full-width">
          <h2>Recent Orders</h2>
          {displayAnalytics.recentOrders && displayAnalytics.recentOrders.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Payment</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayAnalytics.recentOrders.map((order) => (
                    <tr key={order._id}>
                      <td>{order.orderNumber}</td>
                      <td>
                        <div>
                          <div>{order.customer.name}</div>
                          <small>{order.customer.email}</small>
                        </div>
                      </td>
                      <td>{order.items.length} item(s)</td>
                      <td>{formatCurrency(order.total, order.currency)}</td>
                      <td>
                        <span className={`badge badge-${order.status}`}>
                          {order.status}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge-${order.payment.status}`}>
                          {order.payment.status}
                        </span>
                      </td>
                      <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                      <td>
                        <Link to={`/orders/${order._id}`} className="btn btn-sm btn-primary">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state-card">
              <div className="empty-state-icon-small">🛒</div>
              <p className="no-data">No recent orders</p>
              <p className="no-data-hint">Orders will appear here once customers start placing orders</p>
            </div>
          )}
          <div className="card-footer">
            <Link to="/orders" className="btn btn-primary">View All Orders →</Link>
          </div>
        </div>

        {/* Monthly Revenue */}
        <div className="dashboard-card full-width">
          <h2>Monthly Revenue Trend</h2>
          {displayAnalytics.monthlyRevenue && displayAnalytics.monthlyRevenue.length > 0 ? (
            <div className="revenue-chart">
              {displayAnalytics.monthlyRevenue.map((month, index) => {
                const maxRevenue = Math.max(...displayAnalytics.monthlyRevenue.map(m => m.revenue));
                const height = (month.revenue / maxRevenue) * 100;
                const monthName = new Date(month._id.year, month._id.month - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                
                return (
                  <div key={index} className="revenue-bar">
                    <div className="bar-container">
                      <div 
                        className="bar" 
                        style={{ height: `${height}%` }}
                        title={`${monthName}: ${formatCurrency(month.revenue)}`}
                      ></div>
                    </div>
                    <div className="bar-label">
                      <div>{monthName}</div>
                      <div className="bar-value">{formatCurrency(month.revenue)}</div>
                      <div className="bar-orders">{month.orders} orders</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state-card">
              <div className="empty-state-icon-small">📈</div>
              <p className="no-data">No revenue data available</p>
              <p className="no-data-hint">Revenue trends will appear here once you start receiving orders</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <Link to="/products" className="action-card">
            <div className="action-icon">📦</div>
            <div className="action-title">Manage Products</div>
          </Link>
          <Link to="/orders" className="action-card">
            <div className="action-icon">🛒</div>
            <div className="action-title">View Orders</div>
          </Link>
          <Link to="/categories" className="action-card">
            <div className="action-icon">📁</div>
            <div className="action-title">Manage Categories</div>
          </Link>
          <Link to="/banners" className="action-card">
            <div className="action-icon">🖼️</div>
            <div className="action-title">Manage Banners</div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;

